/**
 * MotionBERT 2D→3D pose lifter (PC tier).
 *
 * Window: 243 frames. Input shape: [1, 243, 17, 2]. Output shape:
 * [1, 243, 17, 3] (per-frame world coords, hip-centred metres, y-up).
 *
 * docs/migration-plan.md Phase 12.
 */

import * as ort from 'onnxruntime-web';

import { ensureOrtWasmEnv, fetchModelCached, pickOnnxEPs } from '../onnx-runtime';
import type { PoseLifterAdapter } from '../../core/PoseLifter';

export interface MotionBertOptions {
  /** Override URL for self-hosting. Default: /models/motionbert-243-17.onnx. */
  modelUrl?: string;
}

const DEFAULT_MODEL_URL = '/models/motionbert-243-17.onnx';
const WINDOW_FRAMES = 243;
const NUM_JOINTS = 17;

export class MotionBertLifter implements PoseLifterAdapter {
  readonly id = 'motionbert@1.0';
  readonly windowFrames = WINDOW_FRAMES;
  readonly tier = 'pc' as const;

  private session: ort.InferenceSession | null = null;
  private inputName: string | null = null;
  private outputName: string | null = null;

  constructor(private readonly opts: MotionBertOptions = {}) {}

  async initialize(): Promise<void> {
    if (this.session) return;
    ensureOrtWasmEnv();
    const url = this.opts.modelUrl ?? DEFAULT_MODEL_URL;
    const { list } = pickOnnxEPs();
    const { bytes, diag } = await fetchModelCached(url);
    console.info('[motionbert]', diag.source, `${(diag.byteLength / 1e6).toFixed(1)}MB`, `${diag.ms.toFixed(0)}ms`);
    this.session = await ort.InferenceSession.create(bytes, {
      executionProviders: list,
      graphOptimizationLevel: 'all',
    });
    this.inputName = this.session.inputNames[0];
    this.outputName = this.session.outputNames[0];
  }

  async liftWindow(
    keypoints2D: Float32Array,
    numFrames: number,
    numJoints: number,
  ): Promise<Float32Array | null> {
    if (!this.session || !this.inputName || !this.outputName) return null;
    if (numFrames !== WINDOW_FRAMES || numJoints !== NUM_JOINTS) {
      throw new Error(
        `MotionBertLifter expects [${WINDOW_FRAMES}, ${NUM_JOINTS}, 2]; got [${numFrames}, ${numJoints}, 2]`,
      );
    }
    const tensor = new ort.Tensor('float32', keypoints2D, [
      1,
      WINDOW_FRAMES,
      NUM_JOINTS,
      2,
    ]);
    const result = await this.session.run({ [this.inputName]: tensor });
    const out = result[this.outputName].data as Float32Array;
    if (!out || out.length !== WINDOW_FRAMES * NUM_JOINTS * 3) return null;
    // Return a fresh-owned buffer; the runtime may recycle the
    // backing storage between runs.
    return new Float32Array(out);
  }

  async dispose(): Promise<void> {
    try {
      await this.session?.release?.();
    } catch {
      /* ignore */
    }
    this.session = null;
    this.inputName = null;
    this.outputName = null;
  }
}

